// UserKpiTable.jsx - Table Component for User KPI List

import Pagination from '@libComponents/Pagination';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    Avatar, Box, IconButton, Link, Menu, styled, Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel, Tooltip,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import dayjs from 'dayjs';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { userEmailSelector } from 'auth/redux.selectors';
import { ROWS_PER_PAGE_OPTIONS, TABLE_COLUMNS } from '../common';
import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';

function stringAvatar(name) {
    const nameParts = name.split(' ').filter((part) => part.length > 0);
    return {
        sx: {
            color: '#ffffff',
            width: 32,
            height: 32,
            fontSize: 16
        },
        children:
            nameParts.length >= 2
                ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
                : nameParts.length === 1
                ? nameParts[0][0].toUpperCase()
                : '?'
    };
}

const StyledMenuBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    gap: theme.spacing(2)
}));

const menuLinkStyles = (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    height: '32px',
    padding: '8px 12px',
    border: '1px solid',
    borderColor: 'primary.main',
    borderRadius: '4px',
    color: 'primary.main',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: 400,
    cursor: 'pointer',
    pl: 3,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        textDecoration: 'none',
        borderColor: theme.palette.primary.dark,
        color: theme.palette.primary.dark
    },
    '&:focus': {
        outline: 'none',
        backgroundColor: alpha(theme.palette.primary.main, 0.1)
    }
});

const deleteLinkStyles = (theme) => ({
    ...menuLinkStyles(theme),
    borderColor: 'error.main',
    color: 'error.main',
    '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.1),
        textDecoration: 'none',
        borderColor: 'error.dark',
        color: 'error.dark'
    },
    '&:focus': {
        outline: 'none',
        backgroundColor: alpha(theme.palette.error.main, 0.1)
    }
});

const UserKpiTable = ({ userKpis, pagination, sort, onSort, onPageChange, onPageSizeChange }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
    const [activeUserKpi, setActiveUserKpi] = React.useState(null);
    
    // Get logged-in user email to exclude own KPI from management table
    const loggedInUserEmail = useSelector(userEmailSelector);

    // Get users and periods from Redux to enrich the data
    const allUsers = useSelector(selectors.selectAllUsers) || [];
    const allPeriods = useSelector(selectors.selectAllPeriods) || [];

    const displayUserKpis = React.useMemo(() => {
        if (!Array.isArray(userKpis)) return [];
        if (!loggedInUserEmail) return userKpis;
        return userKpis.filter(kpi => kpi.userId !== loggedInUserEmail);
    }, [userKpis, loggedInUserEmail]);

    const handleManageGoals = (userKpi) => {
        dispatch(actions.openManageGoalsDialog(userKpi));
    };

    const handleScores = (userKpi) => {
        dispatch(actions.openScoreDialog(userKpi));
    };

    const handleEdit = (userKpi) => {
        dispatch(actions.openEditUserKpiDialog(userKpi));
    };

    const handleDelete = (userKpi) => {
        dispatch(actions.openDeleteDialog(userKpi));
    };

    const openMenu = (event, userKpi) => {
        setMenuAnchorEl(event.currentTarget);
        setActiveUserKpi(userKpi);
    };

    const closeMenu = () => {
        setMenuAnchorEl(null);
        setActiveUserKpi(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return dayjs(dateString).format('MMM DD, YYYY');
        } catch {
            return dateString;
        }
    };

    return (
        <>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {TABLE_COLUMNS.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.id === 'actions' ? 'center' : 'left'}
                                    style={{ minWidth: column.minWidth }}
                                    sx={{
                                        fontWeight: 600,
                                        backgroundColor: (theme) => theme.palette.background.childHeader,
                                        borderBottom: 1,
                                        borderBottomColor: 'divider'
                                    }}
                                >
                                    {column.sortable ? (
                                        <TableSortLabel
                                            active={sort.sortBy === column.id}
                                            direction={sort.sortBy === column.id && sort.sortDescending ? 'desc' : 'asc'}
                                            onClick={() => onSort(column.id)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : (
                                        column.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {userKpis.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                    No user KPI assignments found
                                </TableCell>
                            </TableRow>
                        ) : (
                            userKpis.map((userKpi) => {
                                const isSelf = userKpi.userId === loggedInUserEmail;
                                // Enrich user data
                                const user = allUsers.find(u => 
                                    (u.user?.email || u.employeeId) === userKpi.userId
                                );
                                const fullname = user ? [user.firstname, user.middlename, user.lastname].filter(Boolean).join(' ') : '';
                                const userName = user 
                                    ? fullname || user.user?.email || userKpi.userId
                                    : userKpi.userId;
                                
                                // Enrich period data
                                const period = allPeriods.find(p => p.id === userKpi.periodId);
                                const periodName = period?.name || userKpi.periodId || '-';
                                
                                const goalsCount = userKpi.goals?.length || 0;

                                // Calculate overall score
                                const calculateOverallScore = () => {
                                    if (!userKpi.goals || userKpi.goals.length === 0) {
                                        return null;
                                    }

                                    let totalWeightedScore = 0;
                                    let totalWeight = 0;
                                    let totalGoalsWithScores = 0;

                                    userKpi.goals.forEach(goalItem => {
                                        const weight = Number(goalItem.weight) || 0;
                                        
                                        if (goalItem.scores && goalItem.scores.length > 0) {
                                            totalGoalsWithScores++;
                                            totalWeight += weight;
                                            
                                            // Calculate average score for this goal
                                            const goalScores = goalItem.scores;
                                            const totalScore = goalScores.reduce((sum, s) => sum + Number(s.score), 0);
                                            const averageScore = totalScore / goalScores.length;
                                            
                                            // Calculate weighted score: (averageScore / 5) × weight
                                            const weightedScore = (averageScore / 5) * weight;
                                            totalWeightedScore += weightedScore;
                                        }
                                    });

                                    if (totalGoalsWithScores === 0 || totalWeight === 0) {
                                        return null;
                                    }

                                    // Apply same rounding logic: if fractional >= 0.9, ceil
                                    const avgOn5Scale = (totalWeightedScore / totalWeight) * 5;
                                    const fractional = avgOn5Scale - Math.floor(avgOn5Scale);
                                    const finalAvg = fractional >= 0.9 ? Math.ceil(avgOn5Scale) : avgOn5Scale;
                                    
                                    // Calculate percentage: (finalAvg / 5) × 100
                                    const percentageScore = (finalAvg / 5) * 100;

                                    const getRangeLabel = (avg) => {
                                        if (avg >= 4) return 'Great';
                                        if (avg >= 3) return 'Achieving';
                                        if (avg >= 2) return 'Something Achieving';
                                        if (avg >= 1) return 'Requires Improvement';
                                        return 'Failing';
                                    };

                                    return {
                                        percentageScore: Math.round(percentageScore * 10) / 10,
                                        averageScore: finalAvg,
                                        label: `${Math.round(percentageScore * 10) / 10}%`,
                                        rangeLabel: getRangeLabel(finalAvg),
                                        color: finalAvg >= 4 ? 'success' : finalAvg >= 3 ? 'info' : finalAvg >= 2 ? 'warning' : 'error'
                                    };
                                };

                                const scoreData = calculateOverallScore();

                                return (
                                    <TableRow
                                        key={userKpi.id}
                                        hover
                                        sx={(theme) => ({
                                            '&:last-child td': { border: 0 },
                                            '&:hover': { backgroundColor: alpha(theme.palette.divider, 0.06) }
                                        })}
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar {...stringAvatar(userName)} />
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {userName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {userKpi.userId}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{periodName}</TableCell>
                                        <TableCell>{userKpi.designationAtAssignment?.name || '-'}</TableCell>
                                        <TableCell>
                                            <Box
                                                onClick={goalsCount > 0 ? () => handleManageGoals(userKpi) : undefined}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: '50%',
                                                    border: '1px solid',
                                                    borderColor: goalsCount > 0 ? 'primary.main' : 'grey.300',
                                                    backgroundColor: 'transparent',
                                                    color: goalsCount > 0 ? 'primary.main' : 'grey.600',
                                                    fontWeight: 600,
                                                    fontSize: '0.85rem',
                                                    cursor: goalsCount > 0 ? 'pointer' : 'default',
                                                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    '&::before': goalsCount > 0 ? {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'primary.main',
                                                        transform: 'scale(0)',
                                                        transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        zIndex: 0,
                                                        opacity: 1
                                                    } : {},
                                                    ...(goalsCount > 0 && {
                                                        '&:hover': {
                                                            borderColor: 'primary.main',
                                                            transform: 'scale(1.08)',
                                                            boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                                                            '&::before': {
                                                                transform: 'scale(1)',
                                                                opacity: 1
                                                            }
                                                        },
                                                        '&:hover > span': {
                                                            transform: 'scale(1)',
                                                            color: (theme) => theme.palette.getContrastText(theme.palette.primary.main)
                                                        },
                                                        '&:active': {
                                                            transform: 'scale(0.96)',
                                                            boxShadow: (theme) => `0 2px 6px ${alpha(theme.palette.primary.main, 0.18)}`
                                                        }
                                                    })
                                                }}
                                            >
                                                <span style={{ position: 'relative', zIndex: 1, transition: 'color 0.25s ease' }}>
                                                    {goalsCount}
                                                </span>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {scoreData ? (
                                                <Box
                                                    onClick={() => handleScores(userKpi)}
                                                    sx={[
                                                        (theme) => {
                                                            const chipColor = scoreData.color === 'success'
                                                                ? theme.palette.success.main
                                                                : scoreData.color === 'info'
                                                                ? theme.palette.info.main
                                                                : scoreData.color === 'warning'
                                                                ? theme.palette.warning.main
                                                                : theme.palette.error.main;
                                                            return {
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                height: 24,
                                                                border: '1px solid',
                                                                borderColor: chipColor,
                                                                borderRadius: '12px',
                                                                px: 1.5,
                                                                backgroundColor: 'transparent',
                                                                color: chipColor,
                                                                fontWeight: 600,
                                                                fontSize: '0.85rem',
                                                                lineHeight: '24px',
                                                                letterSpacing: 0,
                                                                cursor: 'pointer',
                                                                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                position: 'relative',
                                                                overflow: 'hidden',
                                                                '&::before': {
                                                                    content: '""',
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    right: 0,
                                                                    bottom: 0,
                                                                    borderRadius: '12px',
                                                                    backgroundColor: chipColor,
                                                                    transform: 'scale(0)',
                                                                    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                    zIndex: 0,
                                                                    opacity: 1
                                                                },
                                                                '&:hover': {
                                                                    borderColor: chipColor,
                                                                    transform: 'scale(1.08)',
                                                                    boxShadow: `0 4px 12px ${alpha(chipColor, 0.18)}`,
                                                                    '&::before': {
                                                                        transform: 'scale(1)',
                                                                        opacity: 1
                                                                    }
                                                                },
                                                                '&:hover > span': {
                                                                    transform: 'scale(1)',
                                                                    color: theme.palette.getContrastText(chipColor)
                                                                },
                                                                '&:active': {
                                                                    transform: 'scale(0.96)',
                                                                    boxShadow: `0 2px 6px ${alpha(chipColor, 0.12)}`
                                                                }
                                                            };
                                                        }
                                                    ]}
                                                >
                                                    <span style={{ position: 'relative', zIndex: 1, transition: 'color 0.25s ease' }}>
                                                        {scoreData.label}
                                                    </span>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.disabled">
                                                    -
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{userKpi.updatedBy || userKpi.createdBy || '-'}</TableCell>
                                        <TableCell>{formatDate(userKpi.updatedAt)}</TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Tooltip title="View Details" placement="top">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`/kpis/dashboard?userId=${userKpi.userId}&kpiperiodid=${userKpi.periodId}`)}
                                                        sx={{
                                                            color: 'inherit',
                                                            '&:hover': {
                                                                backgroundColor: (theme) => alpha(theme.palette.divider, 0.06)
                                                            }
                                                        }}
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={isSelf ? "Cannot score your own KPI" : "Manage Scores"} placement="top">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            disabled={isSelf}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                if (!isSelf) {
                                                                    handleScores(userKpi);
                                                                    closeMenu();
                                                                }
                                                            }}
                                                            sx={{
                                                                color: 'inherit',
                                                                '&:hover': {
                                                                    backgroundColor: (theme) => alpha(theme.palette.divider, 0.06)
                                                                }
                                                            }}
                                                        >
                                                            <AssessmentIcon sx={{fontSize: '20px'}} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                {!isSelf && (
                                                    <Tooltip title="More" placement="top">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => openMenu(e, userKpi)}
                                                        >
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu
                id="user-kpi-actions-menu"
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={closeMenu}
                MenuListProps={{
                    'aria-labelledby': 'user-kpi-actions-button'
                }}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >          
                <StyledMenuBox>
                    <Link
                        component="button"
                        onClick={(e) => {
                            e.preventDefault();
                            if (activeUserKpi) handleManageGoals(activeUserKpi);
                            closeMenu();
                        }}
                        sx={(theme) => menuLinkStyles(theme)}
                    >
                        <ListAltIcon sx={{ marginRight: 1, fontSize: '18px' }} />
                        Manage Goals
                    </Link>
                    <Link
                        component="button"
                        onClick={(e) => {
                            e.preventDefault();
                            if (activeUserKpi) handleEdit(activeUserKpi);
                            closeMenu();
                        }}
                        sx={(theme) => menuLinkStyles(theme)}
                    >
                        <EditIcon sx={{ marginRight: 1, fontSize: '18px' }} />
                        Edit
                    </Link>
                    <Link
                        component="button"
                        onClick={(e) => {
                            e.preventDefault();
                            if (activeUserKpi) handleDelete(activeUserKpi);
                            closeMenu();
                        }}
                        sx={(theme) => deleteLinkStyles(theme)}
                    >
                        <DeleteIcon sx={{ marginRight: 1, fontSize: '18px' }} />
                        Delete
                    </Link>
                </StyledMenuBox>
            </Menu>

            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', borderTop: 1, borderColor: 'divider', pt: 1 }}>
                <Pagination
                    count={Math.ceil(pagination.total / pagination.limit)}
                    onChange={(event, newPage) => onPageChange(newPage)}
                    page={pagination.offset || 1}
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                    isRowsPerPageVarient={true}
                    rowsPerPageProps={{
                        name: 'kpi-rows-per-page',
                        onChange: (value) => onPageSizeChange(parseInt(value, 10)),
                        value: pagination.limit
                    }}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                />
            </Box>
        </>
    );
};

export default UserKpiTable;